// SoilInfoComponent.js
import { useState, useEffect, useRef } from "react";
import {
  EditableSelectComponent,
  EditableTextareaComponent,
  SectionHeader,
} from "components";
import { Document } from "assets/icons";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { FileLabel } from "shared/types/file";
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";
import { BuildingDTO } from "shared/types/building";
import {
  BuildingRegulatoryInfoDTO,
  GScore,
  PScore,
  RiskActivities,
  SoilStatus,
} from "shared/types/regulatory-info";
import { UnitDTO } from "shared/types/unit";
import FileLogTable from "shared/components/uploadFileModal/fileLogTable";

interface SoilInfoComponentProps {
  buildingRegulatoryInfo: BuildingRegulatoryInfoDTO;
  handleSoilInfoChangeInParent?: (value: any) => any;
  handleSaveInParent?: () => any;
  handleCloseParent?: () => any;
  building: BuildingDTO;
  unit: UnitDTO;
  parentEditMode?: boolean;
}

const SoilInfoComponent = ({
  buildingRegulatoryInfo,
  handleCloseParent,
  handleSaveInParent,
  building,
  unit,
  handleSoilInfoChangeInParent,
  parentEditMode = true,
}: SoilInfoComponentProps) => {
  const [editMode, setEditMode] = useState(parentEditMode);
  const [editedSoilInfo, setEditedSoilInfo] = useState(buildingRegulatoryInfo);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { session } = useSession();
  const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
  const [selectedFileLabel, setSelectedFileLabel] = useState<FileLabel | null>(
    null,
  );
  const fileLogTableRef = useRef(null);

  // Define the soil document file labels
  const soilFileLabels = [FileLabel.Bodemattest];

  useEffect(() => {
    setEditedSoilInfo(buildingRegulatoryInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch

  const handleSave = async () => {
    // Save any changes in the FileLogTable first
    if (fileLogTableRef.current && (fileLogTableRef.current as any).hasUnsavedChanges) {
      await (fileLogTableRef.current as any).saveChanges();
    }
    
    setHasUnsavedChanges(false);
    try {
      if (handleSaveInParent) {
        handleSaveInParent();
      } else {
        await fetchWithAuth(
          `${API_URL}/building/soil-info`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(editedSoilInfo),
          },
          session,
        );
        toast.success("Bodem informatie succesvol bijgewerkt!");
      }
    } catch (e) {
      console.error(e);
      toast.error(
        "Er is een fout opgetreden bij het bijwerken van de bodem informatie.",
      );
    }
  };

  const handleUploadClick = (fileLabel: FileLabel) => {
    setSelectedFileLabel(fileLabel);
    setFileModalOpen(true);
  };

  const handleFileUploadSuccess = async (fileLogData: any) => {
    const { fileLog } = fileLogData;
    let updatedField = {};

    if (selectedFileLabel === FileLabel.Bodemattest) {
      updatedField = {
        soilCertificateFileLogId: fileLog.id,
        soilCertificateFileLog: fileLog,
      };
    }

    const updatedSoilInfo = { ...buildingRegulatoryInfo, ...updatedField };

    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/soil-info`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedSoilInfo),
        },
        session,
      );

      if (response.ok) {
        toast.success("Bodem informatie succesvol geüpdatet");
        setHasUnsavedChanges(false);
        setEditedSoilInfo(updatedSoilInfo);
      } else {
        throw new Error("Failed to update Soil information");
      }
    } catch (error) {
      console.error("Error updating Soil information:", error);
      toast.error("Failed to update Soil information");
    }
  };

  const updateSoilInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedSoilInfo((prev: any) => ({ ...prev, ...updatedFields }));

    if (handleSoilInfoChangeInParent) {
      handleSoilInfoChangeInParent({
        ...buildingRegulatoryInfo,
        ...updatedFields,
      });
    }
  };

  const getInputFieldStatusColor = (value: any) => {
    return value ? "bg-green-500" : "bg-transparent-500"; // Green for filled, transparent for empty
  };

  const getStatusColor = (status: any) => {
    switch (status) {
      case SoilStatus.NO_POLLUTION:
      case SoilStatus.NO_INFORMATION:
      case RiskActivities.NO_INFORMATION:
      case RiskActivities.NO_RISK_SOIL:
      case PScore.A:
      case PScore.B:
      case GScore.A:
      case GScore.B:
        return "bg-green-500"; // Green for OK
      case RiskActivities.RISK_SOIL:
      case RiskActivities.RISK_SOIL_PAST:
      case PScore.C:
      case GScore.C:
        return "bg-orange-500"; // Yellow for Partial
      case SoilStatus.REMEDIATION_REQUIRED_OWN:
      case SoilStatus.REMEDIATION_REQUIRED_THIRD_PARTY:
      case PScore.D:
      case GScore.D:
        return "bg-red-500"; // Red for Not OK
      default:
        return "bg-transparent-500";
    }
  };

  return (
    <section className="flex flex-col gap-0 rounded-md bg-white">
      <SectionHeader
        title="Bodem Informatie"
        icon={<Document />}
        editMode={editMode}
        sticky={false}
        onEditClick={() => setEditMode(!editMode)}
        onSaveClick={handleSave}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseClick={() => {
          if (handleCloseParent) handleCloseParent();
        }}
        hideCloseAction={true}
      />
      <div className="p-3">
        <div className="flex flex-col gap-4">
          <div className="gap-3 sm:grid sm:grid-cols-2">
            {/* Soil Status */}
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <span
                  className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                    editedSoilInfo?.soilStatus,
                  )}`}
                />

                <p>Bodemstatus</p>
              </header>
              <EditableSelectComponent
                label="Bodemstatus"
                value={editedSoilInfo.soilStatus}
                options={Object.values(SoilStatus).map((status: any) => ({
                  label: status,
                  value: status,
                }))}
                editMode={editMode}
                onChange={(value) => updateSoilInfo({ soilStatus: value })}
              />
            </div>

            {/* Risk Activities */}
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <span
                  className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                    editedSoilInfo?.soilActivities,
                  )}`}
                />

                <p>Aanwezigheid van risicoactiviteiten?</p>
              </header>
              <EditableSelectComponent
                label="Risico-activiteiten"
                value={editedSoilInfo.soilActivities}
                options={Object.values(RiskActivities).map((activity: any) => ({
                  label: activity,
                  value: activity,
                }))}
                editMode={editMode}
                onChange={(value) => updateSoilInfo({ soilActivities: value })}
              />
            </div>
            {/* Soil Status Remarks */}
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <span
                  className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getInputFieldStatusColor(
                    editedSoilInfo.soilStatusRemarks,
                  )}`}
                />

                <p>Opmerkingen bij de bodemtoestand</p>
              </header>
              <EditableTextareaComponent
                label="Opmerkingen over bodemstatus"
                value={editedSoilInfo.soilStatusRemarks}
                editMode={editMode}
                isLoading={false}
                onChange={(value) => updateSoilInfo({ soilStatusRemarks: value })}
              />
            </div>
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <span
                  className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                    editedSoilInfo?.pScore,
                  )}`}
                />
                <p>P-Score</p>
              </header>
              {/* P-Score */}
              <EditableSelectComponent
                label="P-Score"
                value={editedSoilInfo.pScore}
                options={Object.values(PScore).map((score: any) => ({
                  label: score,
                  value: score,
                }))}
                editMode={editMode}
                onChange={(value) => updateSoilInfo({ pScore: value })}
              />
            </div>
            {/* G-Score */}
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <span
                  className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                    editedSoilInfo?.gScore,
                  )}`}
                />

                <p>G-Score</p>
              </header>
              <EditableSelectComponent
                label="G-Score"
                value={editedSoilInfo.gScore}
                options={Object.values(GScore).map((score: any) => ({
                  label: score,
                  value: score,
                }))}
                editMode={editMode}
                onChange={(value) => updateSoilInfo({ gScore: value })}
              />
            </div>
          </div>
          
          {/* Soil Documents Table */}
          <div className="mt-4">
            <h3 className="mb-2 text-base font-medium">Bodem documenten</h3>
            <FileLogTable
              ref={fileLogTableRef}
              acceptedLabels={soilFileLabels}
              onUploadClick={() => handleUploadClick(FileLabel.Bodemattest)}
              building={building}
              unit={unit}
            />
          </div>

          {/* Modal for File Upload - still used when uploading new files */}
          <ModalUploadFileLog
            isOpen={fileModalOpen}
            onClose={() => setFileModalOpen(false)}
            onSave={handleFileUploadSuccess}
            defaultFileLabel={selectedFileLabel as FileLabel}
            building={building}
            unit={unit}
            customLabels={[selectedFileLabel]}
          />
        </div>
      </div>
    </section>
  );
};

export default SoilInfoComponent;
